import { useState, useEffect } from 'react'
import api from '../../services/api'
import toast from 'react-hot-toast'
import { Users, Mail, Phone, BookOpen, TrendingUp } from 'lucide-react'
import LoadingSpinner from '../../components/LoadingSpinner'

export default function Students() {
  const [students, setStudents] = useState([])
  const [courses, setCourses] = useState([])
  const [selectedCourse, setSelectedCourse] = useState('all')
  const [loading, setLoading] = useState(true)
  const [currentPage, setCurrentPage] = useState(1)

  useEffect(() => {
    fetchCourses()
  }, [])

  useEffect(() => {
    if (selectedCourse) {
      fetchStudents()
    }
  }, [selectedCourse])

  const fetchCourses = async () => {
    try {
      const { data } = await api.get('/trainers/courses')
      setCourses(data)
      if (data.length > 0) {
        setSelectedCourse(data[0].id)
      }
    } catch (error) {
      toast.error('Failed to load courses')
      setLoading(false)
    }
  }

  const fetchStudents = async () => {
    if (selectedCourse === 'all') return
    
    try {
      setLoading(true)
      const { data } = await api.get(`/trainers/courses/${selectedCourse}/students`)
      setStudents(data)
    } catch (error) {
      toast.error('Failed to load students')
    } finally {
      setLoading(false)
    }
  }

  if (loading) return <LoadingSpinner label="Loading students" />

  const ITEMS_PER_PAGE = 9
  const totalPages = Math.ceil(students.length / ITEMS_PER_PAGE)
  const safePage = Math.min(currentPage, Math.max(1, totalPages))
  const currentStudents = students.slice((safePage - 1) * ITEMS_PER_PAGE, safePage * ITEMS_PER_PAGE)

  return (
    <div className="space-y-8">
      <div className="cinema-page-header">
        <div>
          <span className="cinema-label block mb-0.5">Trainer Portal</span>
          <h1 className="font-display text-xl text-white font-light">My Students</h1>
        </div>
      </div>

      {/* Course selector */}
      <div>
        <label className="cinema-label block mb-2">Select Course</label>
        <select
          value={selectedCourse}
          onChange={(e) => setSelectedCourse(e.target.value)}
          className="w-full max-w-md bg-white/5 border border-white/10 text-white text-sm font-sans px-3 py-2.5 focus:outline-none focus:border-cinema-glow/40 transition-colors"
        >
          {courses.map((course) => (
            <option key={course.id} value={course.id} className="bg-gray-900">
              {course.title} ({course.enrolled_students || 0} students)
            </option>
          ))}
        </select>
      </div>

      {students.length === 0 ? (
        <div className="cinema-empty">
          <Users size={36} className="mx-auto mb-4 text-cinema-muted/20" />
          <p className="font-sans text-sm text-cinema-muted">No students enrolled in this course</p>
        </div>
      ) : (
        <>
          <div className="cinema-list">
            {currentStudents.map((student) => {
              const pct = student.completion_percentage || 0
              const initials = `${student.first_name?.[0] || ''}${student.last_name?.[0] || ''}`.toUpperCase()
              return (
                <div key={student.id} className="cinema-list-row flex-col !items-stretch py-4">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 rounded-sm bg-cinema-glow/15 flex items-center justify-center text-cinema-glow font-display text-sm font-semibold shrink-0">
                      {initials}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-sans text-sm sm:text-base text-white font-medium truncate">{student.first_name} {student.last_name}</p>
                      <p className="font-sans text-xs text-cinema-muted mt-0.5 font-medium">STU-{student.id.toString().padStart(4, '0')}</p>
                    </div>
                  </div>

                  <div className="space-y-2 mb-3">
                    <div className="flex items-center gap-2 font-sans text-xs sm:text-sm text-cinema-muted">
                      <Mail size={13} className="shrink-0" /> <span className="truncate">{student.email}</span>
                    </div>
                    {student.phone && (
                      <div className="flex items-center gap-2 font-sans text-xs sm:text-sm text-cinema-muted">
                        <Phone size={13} className="shrink-0" /> {student.phone}
                      </div>
                    )}
                    <div className="flex items-center gap-2 font-sans text-xs sm:text-sm text-cinema-muted">
                      <BookOpen size={13} className="shrink-0" /> Enrolled: {new Date(student.enrollment_date).toLocaleDateString()}
                    </div>
                  </div>

                  <div className="pt-3 border-t border-white/6">
                    <div className="flex items-center justify-between mb-1.5">
                      <span className="cinema-label">Progress</span>
                      <span className="font-sans text-xs sm:text-sm text-white font-medium">{pct}%</span>
                    </div>
                    <div className="w-full bg-white/5 h-1.5">
                      <div className="h-1.5 bg-cinema-glow transition-all" style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-3 mt-4">
              <button
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={safePage === 1}
                className="p-2 border border-white/10 text-cinema-muted hover:text-white hover:border-cinema-glow/30 disabled:opacity-30 transition-all"
              >
                <TrendingUp size={14} className="rotate-180" />
              </button>
              <span className="font-sans text-xs text-cinema-muted tracking-widest uppercase">{safePage} / {totalPages}</span>
              <button
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={safePage === totalPages}
                className="p-2 border border-white/10 text-cinema-muted hover:text-white hover:border-cinema-glow/30 disabled:opacity-30 transition-all"
              >
                <TrendingUp size={14} />
              </button>
            </div>
          )}
        </>
      )}
    </div>
  )
}
